/**
 * Tujuan   : Navbar portal dengan menu, ganti bahasa, dan launcher Qismo Chatbot
 * Caller   : Layout.js
 * Dependensi: react-redux, react-router-dom, Qismo v5 SDK, assets/img
 * Main Functions: Navbar, handleChatClick, toggleQismoIframe, initQismoIframe
 * Side Effects: inject script Qismo, localStorage qismo-widget, DOM event listeners
 */
import routes from "components/routes";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getMenuApi, setNavbarActive, setLanguage } from "utils/redux/global/globalActions";
import Image from "./Image";
import { useDispatch, useSelector } from "react-redux";
import { NewLogo, PipoBot } from "assets/img";
import { RouterMatches } from "utils/helper/RouterMatches";
import LoadingFallback from "components/views/LoadingFallback";
// import { IcTelepon } from "assets/svg";
import { getDictionary } from "./handle-language/dictionaries";

const hiddenAdminPaths = ["internal-document-admin", "internal-flyer-admin"];

const isHiddenAdminPath = (path) =>
  hiddenAdminPaths.some((hiddenPath) => path?.includes(hiddenPath));

const Navbar = () => {
  const dispatch = useDispatch();
  const { navbarActive, menuApi, language } = useSelector((state) => state?.global);
  const { pathname } = useLocation();
  const matches = RouterMatches();
  const { crumbs } = matches;
  const findPath =
    crumbs?.length > 1
      ? crumbs[0]
      : crumbs?.find((val) => val?.path === pathname);
  const [showBurger, setShowBurger] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dict, setDictionary] = useState({});
  const qismoInitialized = React.useRef(false);

  const getQismoDebugState = () => {
    const iframeById = document.querySelector("#qcw-iframe");
    const iframeBySrc = document.querySelector(
      'iframe[src*="omnichannel.qiscus.com"], iframe[src*="qiscus.com"]'
    );
    const launcher = document.querySelector(
      '#qismo-btn, [id*="qismo-widget"], [class*="qismo-widget-launcher"], [class*="qismo-toggle"], [class*="qismo-launcher"], [class*="qismo-btn"]'
    );

    return {
      origin: window.location.origin,
      href: window.location.href,
      userAgent: window.navigator.userAgent,
      hasWindowQismo: typeof window.Qismo !== "undefined",
      iframeById: !!iframeById,
      iframeByIdClass: iframeById?.className || null,
      iframeByIdStyle: iframeById?.getAttribute("style") || null,
      iframeBySrc: !!iframeBySrc,
      iframeBySrcClass: iframeBySrc?.className || null,
      iframeBySrcStyle: iframeBySrc?.getAttribute("style") || null,
      launcher: !!launcher,
      launcherClass: launcher?.className || null,
      launcherStyle: launcher?.getAttribute("style") || null,
      qismoInitialized: qismoInitialized.current,
    };
  };

  const logQismoDebug = (label, extra = {}) => {
    console.info("[QISMO DEBUG]", label, {
      ...getQismoDebugState(),
      ...extra,
    });
  };

  // Inject CSS untuk menyembunyikan launcher/default widget bawaan Qismo
  // Iframe chat tetap dikendalikan oleh Qismo; kita pakai ikon custom di Navbar
  const injectQismoStyles = () => {
    if (document.getElementById('qismo-custom-style')) return;
    const style = document.createElement('style');
    style.id = 'qismo-custom-style';
    style.textContent = `
      /* Sembunyikan launcher bawaan Qismo */
      #qismo-widget,
      #qismo-widget > main,
      .qcw-trigger-btn,
      .qcw-launcher {
        opacity: 0 !important;
        pointer-events: none !important;
      }
      /* Pastikan iframe chat muncul di layer paling atas */
      #qcw-iframe,
      #qcw-welcome-iframe,
      #qcw-login-form-iframe,
      #qcw-channel-form-iframe,
      #qcw-attention-grabber-iframe {
        z-index: 99999 !important;
      }
    `;
    document.head.appendChild(style);
  };

  const getQismoTrigger = () => {
    return document.querySelector('#qismo-widget .qcw-trigger-btn, .qcw-trigger-btn');
  };

  const handleChatClick = (e) => {
    e.preventDefault();
    logQismoDebug("handleChatClick");
    const trigger = getQismoTrigger();
    if (trigger) {
      trigger.click();
      logQismoDebug("handleChatClick:trigger-clicked");
    } else {
      logQismoDebug("handleChatClick:trigger-not-found");
      const iframe = document.querySelector('#qcw-iframe');
      if (iframe) {
        iframe.style.display = iframe.style.display === 'none' ? 'block' : 'none';
      }
    }
  };

  // Load Qiscus script saat komponen mount (setara DOMContentLoaded)
  useEffect(() => {
    if (!localStorage.getItem('qismo-widget')) {
      localStorage.setItem('qismo-widget', JSON.stringify({
        unique_id: '0895606112704',
        display_name: 'ChatBot',
        extra_fields: JSON.stringify([{ key: 'NPWP', value: '00.000.000.0-001.0002' }]),
      }));
    }
    // Injeksi CSS lebih awal agar Qiscus tidak sempat flash tampil sebelum dikontrol
    injectQismoStyles();
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.src = 'https://omnichannel.qiscus.com/js/qismo-v5.js';
    s.async = true;
    logQismoDebug("script:append");
    s.onload = function () {
      logQismoDebug("script:onload:before-init");
      // eslint-disable-next-line no-undef
      new Qismo('vvwde-dpeofresrievjji', {
        options: {
          channel_id: 130938,
          mobileBreakPoint: 400,
          extra_fields: [
            {
              name: 'NPWP',
              options: [],
              placeholder: 'npwp',
              required: false,
              type: 'input',
              iconField: 'https://qiscus-sdk.s3.ap-southeast-1.amazonaws.com/public/qismo/date.svg',
            },
          ],
          baseUrl: 'https://omnichannel.qiscus.com',
          qismoIframeUrl: 'https://omnichannel.qiscus.com',
          widgetCustomCSS: `.qcw-user-status { display: none }`,
        },
      });
      logQismoDebug("script:onload:after-init");
      // Mulai pantau iframe setelah Qiscus selesai init
      setTimeout(() => logQismoDebug("script:post-init:500ms"), 500);
      window.setTimeout(() => logQismoDebug("script:post-init:1s"), 1000);
      window.setTimeout(() => logQismoDebug("script:post-init:3s"), 3000);
    };
    s.onerror = function (event) {
      console.error("[QISMO DEBUG] script:onerror", event, getQismoDebugState());
    };
    const t = document.getElementsByTagName('script')[0];
    t.parentNode.insertBefore(s, t);

    // Tangkap postMessage dari Qiscus (tombol X di dalam iframe) dengan validasi origin
    const handleQismoMessage = (event) => {
      if (event.origin?.includes("qiscus")) {
        logQismoDebug("postMessage:received", { eventOrigin: event.origin, data: event.data });
      }
      if (event.origin !== 'https://omnichannel.qiscus.com') return;
      if (!event.data) return;
      const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
      if (data.includes('close') || data.includes('minimize') || data.includes('hide')) {
        const trigger = getQismoTrigger();
        if (trigger) {
          trigger.click();
          logQismoDebug("postMessage:trigger-clicked-to-close");
        }
      }
    };
    const handleWindowError = (event) => {
      console.error("[QISMO DEBUG] window:error", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        state: getQismoDebugState(),
      });
    };
    const handleUnhandledRejection = (event) => {
      console.error("[QISMO DEBUG] unhandledrejection", {
        reason: event.reason,
        state: getQismoDebugState(),
      });
    };
    const handleSecurityPolicyViolation = (event) => {
      console.error("[QISMO DEBUG] securitypolicyviolation", {
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        effectiveDirective: event.effectiveDirective,
        originalPolicy: event.originalPolicy,
        sourceFile: event.sourceFile,
        state: getQismoDebugState(),
      });
    };
    window.addEventListener('message', handleQismoMessage);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('securitypolicyviolation', handleSecurityPolicyViolation);
    return () => {
      window.removeEventListener('message', handleQismoMessage);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('securitypolicyviolation', handleSecurityPolicyViolation);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tutup chat jika klik di luar
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (e.target.closest('#kt_quick_chat_toggle')) return;
      const iframes = document.querySelectorAll('#qcw-iframe, #qcw-welcome-iframe, #qcw-login-form-iframe, #qcw-channel-form-iframe, #qcw-attention-grabber-iframe');
      const anyOpen = Array.from(iframes).some((iframe) => iframe && iframe.style.display !== 'none');
      if (!anyOpen) return;
      if (e.target.closest('#qcw-iframe, #qcw-welcome-iframe, #qcw-login-form-iframe, #qcw-channel-form-iframe, #qcw-attention-grabber-iframe')) return;
      const trigger = getQismoTrigger();
      if (trigger) trigger.click();
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper skeleton components
  const Skel = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );
  const SkelNavItem = () => <Skel className="h-6 w-20" />;
  const SkelLogo = () => <Skel className="h-12 w-32" />;
  const SkelButton = () => <Skel className="h-7 w-20 rounded-2xl" />;
  const SkelLanguage = () => <Skel className="h-6 w-16" />;

  const navbarClick = (value) => {
    dispatch(setNavbarActive(value));
  };

  useEffect(() => {
    dispatch(getMenuApi()).then(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (navbarActive !== pathname) {
      navbarClick(findPath?.path);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem("language", language);
    const loadDictionary = async () => {
      try {
        const loadedDictionary = await getDictionary(language);
        setDictionary(loadedDictionary);
      } catch (error) {
        console.error("Error loading dictionary:", error);
      }
    };
    loadDictionary();
  }, [language]);

  const ChangeLanguage = (lang) => {
    dispatch(setLanguage(lang));
  };

  const kamus = (val) => {
    switch (val) {
      case "Home":
        return dict.home;
      case "Tender":
        return dict.tender;
      case "Registrasi":
        return dict.register;
      case "FAQ":
        return dict.faq;
      case "Unduh":
        return dict.download;
      case "Hubungi Kami":
        return dict.contactUs;
      case "E-Catalogue":
        return dict.eCatalog;
      default:
        break;
    }
  };

  return (
    <>
      <div className="flex flex-row items-center justify-between py-4 px-2 bg-primary text-white font-inter">
        <div className="text-sm font-semibold not-italic flex justify-start">
          <h1>Pelindo E-Procurement</h1>
        </div>
        <div className="order-last flex justify-end">
          <div className="flex flex-row items-center font-bold">
            <div className="flex flex-row items-center">
              {/* <div className="mr-2">
                <IcTelepon />
              </div> */}
              {/* <div>(+62) 800 11 99999</div> */}
            </div>
            <div className="px-4 relative">
              {loading ? (
                <SkelLanguage />
              ) : (
                <div className="flex flex-row items-center">
                  <button
                    onClick={() => ChangeLanguage("indo")}
                    className={`${
                      language === "indo" ? "text-white" : "text-white"
                    }`}
                  >
                    IND
                    {language === "indo" && (
                      <hr className="absolute bottom-0 w-6 h-[1px] bg-white mt-2 ml-[2px]" />
                    )}
                  </button>
                  <span className="mx-[2px]"> | </span>
                  <button
                    onClick={() => ChangeLanguage("en")}
                    className={`${
                      language === "en" ? "text-white" : "text-white"
                    }`}
                  >
                    EN
                    {language === "en" && (
                      <hr className="absolute bottom-0 w-6 h-[1px] bg-white mt-2" />
                    )}
                  </button>
                </div>
              )}
            </div>
            <div
              id="kt_quick_chat_toggle"
              className="relative flex items-center justify-center mr-2 cursor-pointer group"
              onClick={handleChatClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                width="26"
                height="26"
                stroke="white"
                fill="white"
              >
                <path d="M87.49 380c1.19-4.38-1.44-10.47-3.95-14.86a44.86 44.86 0 00-2.54-3.8 199.81 199.81 0 01-33-110C47.65 139.09 140.73 48 255.83 48 356.21 48 440 117.54 459.58 209.85a199 199 0 014.42 41.64c0 112.41-89.49 204.93-204.59 204.93-18.3 0-43-4.6-56.47-8.37s-26.92-8.77-30.39-10.11a31.09 31.09 0 00-11.12-2.07 30.71 30.71 0 00-12.09 2.43l-67.83 24.48a16 16 0 01-4.67 1.22 9.6 9.6 0 01-9.57-9.74 15.85 15.85 0 01.6-3.29z" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="32" />
              </svg>
              {/* Tooltip dengan gambar Pipo Bot */}
              <div className="absolute bottom-[-110px] right-0 z-50 hidden group-hover:flex flex-col items-center bg-white rounded-xl shadow-lg p-2 w-28 pointer-events-none">
                <img src={PipoBot} alt="Pipo Bot" className="w-12 h-12 object-contain" />
                <span className="text-primary text-xs font-semibold mt-1 text-center">Pipo Chat Support</span>
              </div>
            </div>
            {loading ? (
              <SkelButton />
            ) : (
              <button className="bg-white rounded-2xl text-primary w-20 h-7 py-1 leading-5">
                <Link to={"/login"}>{dict.login}</Link>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="relative flex flex-row items-center justify-between h-[76px] px-2 md:px-10 font-inter shadow-md">
        <Link to={"/home"}>
          {loading ? (
            <SkelLogo />
          ) : (
            <Image
              src={NewLogo}
              alt="logo-pproc"
              style={{ width: "130px", height: "auto" }}
            />
          )}
        </Link>

        {loading ? (
          <div className="hidden md:flex flex-row gap-6">
            {Array.from({ length: 6 }).map((_, idx) => (
              <SkelNavItem key={idx} />
            ))}
          </div>
        ) : (
          <div className="hidden md:flex flex-row gap-6">
          {menuApi?.filter((val) => !isHiddenAdminPath(val?.path)).map((val, idx) => (
              <div
                key={idx}
                className={`relative ${
                  val?.path === navbarActive
                    ? "text-primary"
                    : "text-font-secondary"
                } font-medium`}
              >
                <Link
                  to={val?.path}
                  onClick={() => navbarClick(val?.path)}
                  target={val?.target}
                >
                  {kamus(val?.title)}
                </Link>
                {val?.path === navbarActive && (
                  <hr className="w-full absolute -bottom-2 h-1 bg-primary border-0 mt-2" />
                )}
              </div>
            ))}
            {/* e-Katalog external link */}
            <div className="relative text-font-secondary font-medium">
              <a href={process.env.REACT_APP_ECATALOG_URL}>{kamus("E-Catalogue")}</a>
            </div>
          </div>
        )}
        {/* burger menu */}
        {loading ? (
          <div className="space-y-1 group md:hidden">
            <Skel className="w-6 h-1" />
            <Skel className="w-6 h-1" />
            <Skel className="w-6 h-1" />
          </div>
        ) : (
          <button
            className="space-y-1 group md:hidden"
            onClick={() => setShowBurger(!showBurger)}
          >
            {/* burger */}
            <div className="w-6 h-1 bg-font-secondary"></div>
            <div className="w-6 h-1 bg-font-secondary"></div>
            <div className="w-6 h-1 bg-font-secondary"></div>

            {showBurger && (
              <div className="bg-white w-screen absolute top-[76px] right-0 z-[9999] shadow-md">
                <div className="flex flex-col divide-y">
                  {routes?.filter((val) => !isHiddenAdminPath(val?.path)).map((val, idx) => {
                    return (
                      <div
                        key={idx}
                        className={`p-2 ${
                          val?.path === navbarActive
                            ? "text-primary"
                            : "text-font-secondary"
                        } font-medium `}
                      >
                        <Link
                          to={val?.path}
                          onClick={() => navbarClick(val?.path)}
                          target={val?.target}
                        >
                          {val?.title}
                        </Link>
                      </div>
                    );
                  })}
                  {/* e-Katalog external link */}
                  <div className="p-2 text-font-secondary font-medium">
                    <a href={process.env.REACT_APP_ECATALOG_URL}>{dict.eCatalog || "e-Katalog"}</a>
                  </div>
                </div>
              </div>
            )}
          </button>
        )}
      </div>
    </>
  );
};

export default Navbar;
